import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Popover, PopoverContent, PopoverTrigger } from '@monksflow/monks-ui';
import { format } from 'date-fns';
import { ArrowLeftRight, Check, ChevronDown, GitBranch, History, RotateCcw } from 'lucide-react';
import { TaxonomyStatusBadge, type TaxonomyStatus } from '@/entities/taxonomy';

/**
 * The minimum a version needs to appear in the menu.
 *
 * Structural rather than tied to `TaxonomyVersion` so Lists — which are
 * versioned the same way but carry columns/rows instead of fields — reuse this
 * switcher instead of growing a parallel one.
 */
export interface VersionMenuEntry {
  id: string;
  versionNumber: number;
  status: TaxonomyStatus;
  createdAt: string;
}

export interface VersionMenuProps {
  versions: VersionMenuEntry[];
  /** The version currently being viewed. */
  selectedId: string;
  /** The taxonomy's head (current) version — marked "Current" in the list. */
  headId: string;
  /** Whether a new version can be branched (false while a draft is in progress). */
  canCreate: boolean;
  /**
   * Whether the viewer may write at all. Kept apart from {@link canCreate} so
   * the "finish or publish the current draft first" hint stays true: it explains
   * a disabled button by naming the draft in the way, which is the wrong reason
   * to give someone whose permissions are what stopped them. Without this, the
   * branching action is absent rather than disabled-and-misexplained.
   */
  canManage: boolean;
  onSelect: (versionId: string) => void;
  onCreateVersion: () => void;
  /** Opens the full audit trail (who/when/why) — the History drawer. */
  onOpenHistory: () => void;
  /**
   * Opens the version-diff comparison picker.
   *
   * Optional because only the SCHEMA side has a diff to open. A schema version
   * differs from its parent by fields and rules, which `diffVersions` is typed
   * over; a LIST version differs by columns and rows, which nothing compares.
   * The list side omits this rather than wiring the control to the History
   * drawer, which is what it used to do — a button that silently did something
   * else.
   */
  onCompare?: () => void;
  /**
   * Roll back to the version being viewed. Offered only when viewing a NON-head
   * version, because rollback here is branch-forward: it creates a new version
   * carrying the old content, leaving the superseded ones intact for anything
   * pinned to them.
   */
  onRestore?: (versionId: string) => void;
}

/**
 * Version switcher for the taxonomy header — mirrors the projects "Versioning"
 * popover (asset detail): the trigger shows the viewed version + status; the
 * popover lists every version (newest first) to switch to and view, marks the
 * head as "Current", and offers "New version" as the primary action inside the
 * same menu (rather than a separate header button), matching that workflow.
 */
export function VersionMenu(props: VersionMenuProps) {
  const { t } = useTranslation('knowledge');
  const {
    versions,
    selectedId,
    headId,
    canCreate,
    canManage,
    onSelect,
    onCreateVersion,
    onOpenHistory,
    onCompare,
    onRestore,
  } = props;
  const [open, setOpen] = useState(false);

  const sorted = useMemo(
    () => [...versions].sort((a, b) => b.versionNumber - a.versionNumber),
    [versions],
  );
  const selected = versions.find((v) => v.id === selectedId);

  const select = (id: string) => {
    onSelect(id);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="default" className="gap-1.5">
          {t('taxonomy.common.versionLabel', { number: selected?.versionNumber ?? '?' })}
          {selected && <TaxonomyStatusBadge status={selected.status} />}
          <ChevronDown className="size-4 text-muted-foreground" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between border-b border-border-light py-1.5 pl-3 pr-1.5">
          <span className="text-sm font-semibold text-foreground">
            {t('taxonomy.versionMenu.versioning')}
          </span>
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              onOpenHistory();
            }}
            className="flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          >
            <History className="size-3.5" />
            {t('taxonomy.versionMenu.history')}
          </button>
        </div>

        <ul className="max-h-72 overflow-y-auto p-1">
          {sorted.map((version) => {
            const isSelected = version.id === selectedId;
            const isCurrent = version.id === headId;
            return (
              <li key={version.id}>
                <button
                  type="button"
                  onClick={() => select(version.id)}
                  className={`flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left transition-colors ${
                    isSelected ? 'bg-accent' : 'hover:bg-accent/60'
                  }`}
                >
                  <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <span className="flex items-center gap-2 text-sm font-medium text-foreground">
                      {t('taxonomy.versionMenu.versionNumber', { number: version.versionNumber })}
                      {isCurrent && (
                        <span className="rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold text-primary">
                          {t('taxonomy.versionMenu.current')}
                        </span>
                      )}
                    </span>
                    <span className="flex items-center gap-2 text-xs text-muted-foreground">
                      <TaxonomyStatusBadge status={version.status} />
                      {/* eslint-disable-next-line i18next/no-literal-string -- date-fns format pattern */}
                      {format(new Date(version.createdAt), 'MMM d, yyyy')}
                    </span>
                  </span>
                  {isSelected && <Check className="size-4 shrink-0 text-foreground" />}
                </button>
              </li>
            );
          })}
        </ul>

        <div className="border-t border-border-light p-2">
          {onRestore && selectedId !== headId && (
            <Button
              variant="outline"
              size="sm"
              className="mb-2 w-full"
              onClick={() => {
                setOpen(false);
                onRestore(selectedId);
              }}
            >
              <RotateCcw className="size-4" />
              {t('taxonomy.versionMenu.restoreVersion')}
            </Button>
          )}
          <div className="flex gap-2">
            {onCompare && (
              <Button
                variant="outline"
                size="sm"
                className="flex-1"
                disabled={versions.length < 2}
                onClick={() => {
                  setOpen(false);
                  onCompare();
                }}
              >
                <ArrowLeftRight className="size-4" />
                {t('taxonomy.versionMenu.compare')}
              </Button>
            )}
            {canManage && (
              <Button
                size="sm"
                className="flex-1"
                disabled={!canCreate}
                onClick={() => {
                  setOpen(false);
                  onCreateVersion();
                }}
              >
                <GitBranch className="size-4" />
                {t('taxonomy.versionMenu.newVersion')}
              </Button>
            )}
          </div>
          {canManage && !canCreate && (
            <p className="mt-1.5 text-center text-[11px] text-muted-foreground">
              {t('taxonomy.versionMenu.finishPublishCurrentDraftFirst')}
            </p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

VersionMenu.displayName = 'VersionMenu';
